import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFemale, FaUserTie, FaGlobeAsia, FaShieldAlt, FaArrowRight } from 'react-icons/fa';
import AnimatedSection from '../ui/AnimatedSection';
import womenLeaderImg from '../../assets/images/about-women.png';
import managementImg from '../../assets/images/about-management.png';
import networkImg from '../../assets/images/about-network.png';
import complianceImg from '../../assets/images/about-compliance.png';

const pillars = [
  { icon: FaFemale, title: 'Women Empowered Entity', desc: 'Proudly a women lead organisation, driving diversity, empowerment and inclusion in maritime services.', image: womenLeaderImg, highlight: true },
  { icon: FaUserTie, title: 'Experienced Management', desc: 'Decades of collective operational leadership across shipping ports, logistics hubs and supply chains.', image: managementImg },
  { icon: FaGlobeAsia, title: 'Global Agency Network', desc: 'Extensive connections with principals, liner operators, port trusts and terminal partners worldwide.', image: networkImg },
  { icon: FaShieldAlt, title: 'Quality & Regulatory Compliance', desc: 'Uncompromising standards of security, health, environmental protection and statutory clearance.', image: complianceImg }
];

const AboutPreview = () => (
  <section style={{
    padding: '4.5rem 0',
    background: '#FFFFFF',
    borderTop: '1px solid rgba(10,17,40,0.05)',
    borderBottom: '1px solid rgba(10,17,40,0.05)',
    overflow: 'hidden'
  }}>
    <div className="container-xxl px-4 px-lg-5">
      <div className="row align-items-start g-5">

        {/* Left column: Brand Statement & History */}
        <div className="col-lg-5 about-sticky-side">
          <AnimatedSection variant="fadeLeft">
            <span className="section-tag">About ISAAC Group</span>
            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 'clamp(2rem, 4vw, 2.6rem)',
              color: 'var(--navy)',
              lineHeight: 1.15,
              marginTop: '0.5rem',
              marginBottom: '1rem',
              letterSpacing: '-0.02em'
            }}>
              One Partner.<br />Endless Solutions.
            </h2>
            <div className="section-divider" style={{ width: '40px', background: 'var(--orange)', height: '1px', margin: '1.5rem 0' }} />
            
            <p style={{
              color: 'var(--text-body)',
              fontSize: '0.95rem',
              lineHeight: 1.8,
              marginBottom: '1.5rem',
              fontWeight: 400
            }}>
              ISAAC Logistic Solutions India Pvt. Ltd. is a premier women lead organisation delivering
              integrated shipping, logistics and marine solutions with excellence, integrity and
              global standards.
            </p>
            <p style={{
              color: 'var(--text-body)',
              fontSize: '0.95rem',
              lineHeight: 1.8,
              marginBottom: '2.5rem'
            }}>
              We combine decades of maritime operational expertise with a global network of strategic partners to deliver
              seamless, end-to-end logistics solutions that keep your supply chains agile.
            </p>
            
            <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
              <Link to="/about" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.65rem' }}>
                Discover Our Story <FaArrowRight size={10} />
              </Link>
            </motion.div>
          </AnimatedSection>
        </div>

        {/* Right column: 2x2 Grid of Cards */}
        <div className="col-lg-7">
          <div className="row g-4">
            {pillars.map((p, i) => {
              const IconComp = p.icon;
              return (
                <div key={i} className="col-sm-6">
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: 'easeOut' }}
                    whileHover={{ y: -4, borderColor: p.highlight ? 'var(--purple-accent)' : 'var(--orange)' }}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 2,
                      padding: '0 0 1.8rem 0',
                      height: '100%',
                      border: '1px solid rgba(10,17,40,0.05)',
                      boxShadow: 'var(--shadow-sm)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      transition: 'border-color 0.2s ease',
                      overflow: 'hidden'
                    }}
                    className="service-detail-card"
                  >
                    <div>
                      <div style={{
                        width: '100%',
                        overflow: 'hidden',
                        marginBottom: '1.25rem',
                        borderBottom: '1px solid rgba(10,17,40,0.05)',
                        position: 'relative'
                      }}>
                        <img
                          src={p.image}
                          alt={p.title}
                          style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block'
                          }}
                        />
                      </div>
                      <div style={{ padding: '0 1.8rem' }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div style={{
                            width: '28px', height: '28px', borderRadius: 2,
                            background: p.highlight ? 'var(--purple-accent-dim)' : 'var(--orange-dim)',
                            border: p.highlight ? '1px solid var(--purple-accent-border)' : '1px solid var(--orange-border)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <IconComp size={11} color={p.highlight ? 'var(--purple-accent-light)' : 'var(--orange)'} />
                          </div>
                          {p.highlight && (
                            <span className="section-tag" style={{
                              margin: 0,
                              padding: '0.15rem 0.5rem',
                              fontSize: '0.55rem',
                              color: 'var(--purple-accent-light)',
                              background: 'var(--purple-accent-dim)',
                              borderColor: 'var(--purple-accent-border)'
                            }}>
                              Corporate Highlight
                            </span>
                          )}
                        </div>
                        <h5 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, color: 'var(--navy)', fontSize: '0.92rem', marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
                          {p.title}
                        </h5>
                        <p style={{ color: 'var(--text-body)', fontSize: '0.8rem', lineHeight: '1.65', margin: 0, textAlign: 'justify' }}>
                          {p.desc}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  </section>
);

export default AboutPreview;
